/**
 * Company-based lists can resolve, qualify and score their companies first and search for people
 * later (PREC-4400). The choice is made in the list's Find person step and is editable afterwards
 * from the step or List Settings. It is stored as a flag in the list's SOURCE apollo config, like
 * `maxPerCompany` and the tracker's `findPersonOnSignal`.
 */
export enum FindPersonMode {
	/** Today's behaviour: each qualifying company is searched for people as soon as it is added. */
	FIND_NOW = "FIND_NOW",
	/**
	 * Companies are enriched, qualified and scored, but nobody is searched for yet and no
	 * find-person credit is charged. Each company becomes a row on the list's Companies tab; the
	 * user selects the ones they want and runs Enrich -> Find person (PREC-4398).
	 */
	COMPANY_ONLY = "COMPANY_ONLY",
}

/**
 * A product-seeded default is not a customer's choice, so a list with no stored mode (every list
 * created before this feature, and every list whose step never set it) behaves exactly as before.
 */
export const DEFAULT_FIND_PERSON_MODE = FindPersonMode.FIND_NOW;

/**
 * Reads the mode out of a source apollo config, defaulting to today's behaviour. Takes `unknown`
 * because the config is a free-form JSON blob (`apollo.config`) on the API and a `z.any()` on the
 * web; the one read that interprets it lives here.
 */
export function findPersonModeOf(config: unknown): FindPersonMode {
	const mode = (config as { findPersonMode?: unknown } | null | undefined)?.findPersonMode;

	return mode === FindPersonMode.COMPANY_ONLY ? FindPersonMode.COMPANY_ONLY : FindPersonMode.FIND_NOW;
}

/** True when the list resolves companies only and waits for a later bulk Find person. */
export function isCompanyOnlyMode(config: unknown): boolean {
	return findPersonModeOf(config) === FindPersonMode.COMPANY_ONLY;
}

/**
 * The list source types whose company scraper actually honours {@link FindPersonMode} today:
 * the CSV company scraper (`csv-enrichment-company-scraper.service.ts`, PREC-4400), which runs for
 * CSV_ENRICHMENTS and CUSTOM_EVENTS company lists, plus the three company scrapers widened in
 * PREC-4586 — Maps (`map-utils.scraper.service.ts`), Funding rounds
 * (`funding-ma-company-scraper.service.ts`) and Parallel AI
 * (`parallel-ai-company-scraper.service.ts`). Each records Companies-tab rows via
 * `CompanyOnlyPlaceholderService` and skips the person search and its find-person charge.
 *
 * Jobs is deliberately absent: it has its own company scraper and is PREC-4586 part 2. The UI must
 * gate the mode to exactly this set, so the "nothing is charged while the list runs" promise is
 * never shown for a list that would in fact be charged and never produce a Companies tab
 * (a "control does what it names", PREC-4129). This single predicate is what keeps the UI's
 * "may I offer company-only?" and the backend's "do I honour it?" from drifting.
 */
export const COMPANY_ONLY_SUPPORTED_SOURCE_TYPES = [
	"CSV_ENRICHMENTS",
	"CUSTOM_EVENTS",
	"MAPS",
	"FUNDING_ROUNDS",
	"PARALLEL_AI",
] as const;

/**
 * Supported source types whose lists are company lists but never store an `entityType`: Maps,
 * Funding rounds and Parallel AI are created from a source sub-type / scrape level, so their
 * `list.entityType` stays NULL. Their company scrapers only run for company lists, so company-only
 * applies without an entity type to compare. CSV_ENRICHMENTS / CUSTOM_EVENTS do store one and are
 * still held to {@link COMPANY_ONLY_SUPPORTED_ENTITY_TYPE}.
 */
export const COMPANY_ONLY_SOURCES_WITHOUT_ENTITY_TYPE = [
	"MAPS",
	"FUNDING_ROUNDS",
	"PARALLEL_AI",
] as const;

/** The entity type the honouring company scraper runs for — company-only is a company-list choice. */
export const COMPANY_ONLY_SUPPORTED_ENTITY_TYPE = "ORGANIZATION";

/**
 * Whether company-only mode may be offered for a list: its source type is honoured by a company
 * scraper, and it is a company list — an ORGANIZATION list where the source stores an entity type,
 * or one of the company sources that do not. Takes the source and entity types as raw strings
 * because they are separate enums on the API and the web (as with {@link findPersonModeOf} and its
 * `unknown` config); the one decision lives here.
 */
export function supportsCompanyOnlyMode(input: {
	sourceType?: string | null;
	entityType?: string | null;
}): boolean {
	const sourceType = input.sourceType ?? "";

	if (!(COMPANY_ONLY_SUPPORTED_SOURCE_TYPES as readonly string[]).includes(sourceType)) {
		return false;
	}

	if ((COMPANY_ONLY_SOURCES_WITHOUT_ENTITY_TYPE as readonly string[]).includes(sourceType)) {
		return true;
	}

	return input.entityType === COMPANY_ONLY_SUPPORTED_ENTITY_TYPE;
}

/**
 * The state of a company on a company-only list's Companies tab (PREC-4400). Stored in
 * `list_person.companyFindPersonStatus` (a STRING column); NULL on every ordinary lead row, set
 * only on a Companies-tab placeholder row. A renamed or removed value would orphan the rows that
 * carry it, so the values are pinned by a spec. It is deliberately NOT a `DataType.ENUM` column
 * (widening one is a schema change — apps/api/CLAUDE.md §1); the model stores these strings in a
 * `DataType.STRING`.
 */
export enum CompanyFindPersonStatus {
	/** Enriched, qualified and scored; nobody searched for yet. "No person searched yet". */
	NOT_SEARCHED = "NOT_SEARCHED",
	/** A bulk Find person run is searching this company now (PREC-4398). */
	SEARCHING = "SEARCHING",
	/** A search found at least one person; they are leads on the New tab, shown on the company row. */
	PEOPLE_FOUND = "PEOPLE_FOUND",
	/** A search ran and found nobody. Distinct from NOT_SEARCHED ("we never looked"). */
	NO_PERSON_FOUND = "NO_PERSON_FOUND",
	/** The company did not pass the ICP filter, so it was never searched. */
	ICP_MISMATCH = "ICP_MISMATCH",
}

/**
 * The buckets the Companies tab's own Show filter offers. "Blacklisted" is not a
 * {@link CompanyFindPersonStatus} value — a blacklisted company row carries `inclusionStatus`
 * BLACKLIST — so the filter has one more bucket than the status enum.
 */
export enum CompanyShowBucket {
	NOT_SEARCHED = "NOT_SEARCHED",
	SEARCHING = "SEARCHING",
	PEOPLE_FOUND = "PEOPLE_FOUND",
	NO_PERSON_FOUND = "NO_PERSON_FOUND",
	ICP_MISMATCH = "ICP_MISMATCH",
	BLACKLISTED = "BLACKLISTED",
}
